import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { SelectEvent } from '@progress/kendo-angular-layout';
import { DataStateChangeEvent } from '@progress/kendo-angular-grid';
import { AppConfigService } from 'src/app/core/service/app-config.service';
import { UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import moment from 'moment';
import { Dictionary } from 'src/app/core/model/dictionary';
import { OrganizationService } from 'src/app/core/service/organization.service';
import { TabStripComponent } from '@progress/kendo-angular-layout';
import { CBOPaymentsService } from 'src/app/core/service/cbo-payments.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { GridViewManager } from 'src/app/core/model/grid-view-manager';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { TranslateService } from '@ngx-translate/core';
import { ExcelExportData } from '@progress/kendo-angular-excel-export';
import { sortBy } from 'lodash';
import { Subscription } from 'rxjs';
import { MessengerService } from 'src/app/core/service/messenger.service';
import { Organization } from 'src/app/core/model/organization';
import { process } from "@progress/kendo-data-query";
import { DictionaryService } from 'src/app/core/service/dictionary.service';

@Component({
  selector: 'app-cbo-payments-list',
  templateUrl: './cbo-payments-list.component.html',
  styleUrls: ['./cbo-payments-list.component.scss']
})

export class CBOPaymentsListComponent implements OnInit, OnDestroy
{
  @ViewChild('tabstrip') public tabstrip: TabStripComponent;

  public enableDetailsTab = false;
  public count = 0;
  public enableAdvanced = false;
  public pageSizes = [5, 10, 25, 50, 100, 1000];

  public selectedTab = 0;
  public maxDate: Date = moment().toDate();
  public loaded = false;
  public allCBOs = [];
  public cbosFiltered: Dictionary[] = [];
  public orgPaymentStatusFiltered: Dictionary[] = [];
  public searchForm: any;
  public searchTerm: string;

  public selectedCBO: any;
  public detailsTabTitle = '';
  public details = '';

  public view: GridViewManager;

  public cboPayments = [];
  public orgPaymentStatuses = [];
  public organizationPaymentStatuses : number; 

  /**
   * Maximum number of items for virtualization. This is to accelerate the DOM performance.
   */
  public virtualCBO: any = { itemHeight: 30 };

  public selectedResources: number[] = [];

  private messagingSubscription: Subscription;

  constructor(
    private appConfigService: AppConfigService,
    private fb: UntypedFormBuilder,
    private organizationService: OrganizationService,
    private cboPaymentsService: CBOPaymentsService,
    private messageHandlerService: MessageHandlerService,
    private authenticationService: AuthenticationService,
    private translate: TranslateService,
    private messengerService: MessengerService,
    private dictionaryService: DictionaryService,)
  {
    this.view = new GridViewManager(this.appConfigService.getConfig().pageSize);
  }

  public get allOrganizations(): UntypedFormControl
  {
    return this.searchForm.get('allOrganizations');
  }

  public get cbos(): UntypedFormControl
  {
    return this.searchForm.get('cbos');
  }

  public get fromDateSelection(): UntypedFormControl
  {
    return this.searchForm.get('fromDateSelection');
  }

  public get toDateSelection(): UntypedFormControl
  {
    return this.searchForm.get('toDateSelection');
  }

  public get orgPaymentStatus(): UntypedFormControl
  {
    return this.searchForm.get('orgPaymentStatus');
  } 

  public ngOnDestroy(): void
  {
    if (this.messagingSubscription !== undefined) { this.messagingSubscription.unsubscribe(); }
  }

  public ngOnInit(): void
  {
    // Get Org Payment Status
    this.dictionaryService.getCachedOrgPaymentStatus().subscribe({
      next: (data) =>
      {
        this.orgPaymentStatuses = data;      
        this.filterOrgPaymentStatuses('');
      }
    });  
    this.buildForm();
    this.translate.get(['DETAILS']).subscribe({
      next: (res) =>
      {
        this.details = res.DETAILS;
      }
    });

    this.messagingSubscription = this.messengerService.receiveMessage().subscribe({
      next: (message: any) =>
      {
        if (message && message.type && message.type === 'role-changed') 
        {
          this.loadOrganizations(message.data.selectedRole);
        }
      }
    });
    this.loadOrganizations(this.authenticationService.currentUser.role);
  }

  /**
   * Filters the CBOs based on user input.
   */
  public filterCBOs(filter: string): void
  {
    this.searchTerm = filter;
    this.cbosFiltered = this.allCBOs
      .filter(org => org.name.toLowerCase().includes(filter.toLowerCase()));
  }

  public filterOrgPaymentStatuses(filter: string): void
  {
    this.searchTerm = filter;
    this.orgPaymentStatusFiltered =
      this.orgPaymentStatuses.filter(user => user.displayName.toLowerCase().includes(filter.toLowerCase()));
    this.orgPaymentStatus.setValue(null);
  }

  public search()
  {
    this.selectedCBO = [];
    this.enableDetailsTab = false;
    this.detailsTabTitle = this.details;
    this.loadGridData();
  }

  public clearSearch(): void
  {
    this.cbos.setValue(null);
    this.fromDateSelection.setValue(null);
    this.toDateSelection.setValue(null);
    this.orgPaymentStatus.setValue(null);
    this.selectedCBO = [];
    this.enableDetailsTab = false;
    this.detailsTabTitle = this.details;
    this.view.setData([]);
    this.count = 0;
  }

  public buildForm(): void
  {
    // Set form
    this.maxDate = moment().toDate();
    this.searchForm = this.fb.group({
      allOrganizations: { value: null, disabled: false },
      cbos: [{ value: null, disabled: false }, Validators.required],
      fromDateSelection: [{ value: null, disabled: false }, Validators.required],
      toDateSelection: [{ value: null, disabled: false }, Validators.required],
      orgPaymentStatus: { value: null, disabled: false }
    });
    const allOrganizations = this.searchForm.controls.allOrganizations.value === undefined || this.searchForm.controls.allOrganizations.value === null ? false : this.searchForm.controls.allOrganizations.value;
    if (allOrganizations === true)
    {
      this.searchForm.get('cbos').disable();
    }
    this.searchForm.get('allOrganizations').valueChanges.subscribe({
      next: (value: boolean) =>
      {
        if (value)
        {
          this.searchForm.get('cbos').disable();
          this.searchForm.get('cbos').setValue(null);
        }
        else
        {
          this.searchForm.get('cbos').enable();
        }
      }
    });    
    this.loaded = true;
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    this.view.setState(state);
  }

  public onTabSelect(e: SelectEvent): void
  {
    const tabChanged = e.index !== this.selectedTab;
    this.selectedTab = e.index;
    // Automatically refresh
    if (tabChanged && this.selectedTab === 0)
    {
      this.loadGridData();
    }
  }

  /**
   * Handles the row selection change of the Kendo grid.
   *
   * @param selections Kendo grid selected keys.
  */
  public onSelectedKeysChange(selections: Array<number>): void
  {
    if (selections.length > 0)
    {
      const id = selections[0]; // we only allow single selection
      const selectedItem = this.selectedCBO = this.view.data.find(x => x.organizationId === id);
      this.selectedCBO = selectedItem !== undefined ? selectedItem : null;
      if (this.selectedCBO !== null)
      {
        this.detailsTabTitle = this.details.concat(' - ', this.selectedCBO.cbo);
      }
      else
      {
        this.detailsTabTitle = this.details;
      }
    }
    else // if the row is unselected.
    {
      this.selectedCBO = null;
      this.detailsTabTitle = this.details;
    }
    this.enableDetailsTab = this.selectedCBO !== null;
  }

  public dblClickInvoice(event: any): void
  {
    const rowIndex = event.target.parentElement.rowIndex;
    if (typeof rowIndex === 'number' && rowIndex < this.view.data.length)
    {
      if (this.enableDetailsTab)
      {
        this.selectedTab = 1;
        this.tabstrip.selectTab(this.selectedTab);
      }
    }
  }

  public allData(): ExcelExportData
  {
    const currentTake = this.view.state.take;
    this.view.state.take = this.view.gridData.total;   
    const result: ExcelExportData = {
      data: process(this.view.data, this.view.state).data
    };
    this.view.state.take = currentTake;    
    return result;
  }

  private loadGridData(): void
  {
    let selectedOrganizations = [];
    if (this.searchForm.controls.allOrganizations.value === true)
    {
      // all organizations
      selectedOrganizations = this.allCBOs.map(x => x.id);
    }
    else
    {
      if (this.searchForm.controls.cbos.value !== null && this.searchForm.controls.cbos.value.length > 0)
      {
        // selected organizations
        selectedOrganizations = this.cbos.value.map(x => x.id);        
      }
    }

    this.organizationPaymentStatuses = this.orgPaymentStatus !== null && this.orgPaymentStatus.value !== null ? this.orgPaymentStatus.value.map((g: any) => g.id).join(',') : '';
    
    this.view.startLoading();
    this.cboPaymentsService.getCBOPaymentsList(selectedOrganizations, this.fromDateSelection.value, this.toDateSelection.value, this.organizationPaymentStatuses).subscribe({
      next: (data) =>
      {
        this.count = data.length;
        this.view.setData(data);
        this.view.setSort([{ field: 'cbo', dir: 'asc' }]);
        this.allData = this.allData.bind(this);
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      },
      complete: () =>
      {
        this.view.stopLoading();
      }
    });
  }

  private loadOrganizations(role: string): void
  {
    // Get CBOs
    if (role === 'ROLE_REVENUE_MANAGER_OPERATOR')
    {
      this.organizationService.getCachedOrganizations().subscribe({
        next: (data) =>
        {
          this.allCBOs = sortBy(data.filter((x: Organization) => x.active), ['name']);
          this.filterCBOs('');
          if (this.loaded) 
          {
            this.cbos.setValue(null);
          }
        }
      });
    }
    else if (role === 'ROLE_CBO_REVENUE_ANALYST')
    {
      this.organizationService.getBillingOrganizations(this.authenticationService.currentUser.id).subscribe({
        next: (data) =>
        {
          this.allCBOs = sortBy(data.filter((x: Organization) => x.active), ['name']);
          this.filterCBOs('');
          if (this.loaded) 
          {
            this.cbos.setValue(null);
          }
        }
      });
    }
  }
}
